/**
 * VersionList — one row per version, grouped by document (contracts-plan.md
 * §11 tab 2, §4.5 R32, §5.4, §13.8 WP-74).
 *
 * Appendix A exposes no document-list route, so a group's label is the
 * versions' own shared filename stem (this WP's report NOTES: [OWNER],
 * a proper document title would need that route).
 *
 * The chain (§4.5, R32) is built from the flat version list already in
 * hand — no extra request: a row whose id appears as another row's
 * supersedes_version_id reads "superseded by v<n>"; a row with a non-null
 * supersedes_version_id reads "supersedes v<n>".
 *
 * Immutability (R32): direction 'sent' or is_executed true is a record —
 * no supersede action, no executed action, a "record" badge.
 */
import { Fragment } from 'react';
import { Download, CheckCircle2, History, Lock } from 'lucide-react';
import type { VersionWithInventory } from '../../api/versions';
import { InventoryDisclosure } from './InventoryDisclosure';

export interface VersionListProps {
  versions: VersionWithInventory[];
  selected: Set<string>;
  onToggleSelect: (id: string) => void;
  readOnly: boolean;
  onDownload: (id: string) => void;
  onMarkExecuted: (id: string) => void;
  onOpenSupersede: (id: string) => void;
  executingId: string | null;
}

const PARSE_STATUS_CLS: Record<string, string> = {
  pending: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  running: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
  parsed: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  unsupported: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

const DIRECTION_LABEL: Record<string, string> = { received: 'Received', sent: 'Sent', internal: 'Internal' };
const DIRECTION_CLS: Record<string, string> = {
  received: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
  sent: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  internal: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

function filenameStem(filename: string): string {
  return filename.replace(/\.[^./\\]+$/, '');
}

function StatusPill({ text, cls, title }: { text: string; cls: string; title?: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${cls}`}
      title={title}
    >
      {text}
    </span>
  );
}

export function VersionList({
  versions, selected, onToggleSelect, readOnly, onDownload, onMarkExecuted, onOpenSupersede, executingId,
}: VersionListProps) {
  const supersededByVersionNo = new Map<string, number>();
  const supersedesVersionNo = new Map<string, number>();
  const byId = new Map(versions.map((v) => [v.id, v]));
  for (const v of versions) {
    if (v.supersedes_version_id) {
      const target = byId.get(v.supersedes_version_id);
      if (target) {
        supersedesVersionNo.set(v.id, target.version_no);
        supersededByVersionNo.set(v.supersedes_version_id, v.version_no);
      }
    }
  }

  const groups = new Map<string, VersionWithInventory[]>();
  for (const v of versions) {
    const list = groups.get(v.document_id) ?? [];
    list.push(v);
    groups.set(v.document_id, list);
  }
  const groupEntries = [...groups.entries()].map(([documentId, rows]) => {
    const sorted = [...rows].sort((a, b) => a.version_no - b.version_no);
    return { documentId, label: filenameStem(sorted[0].filename), rows: sorted };
  });

  return (
    <div className="space-y-4">
      {groupEntries.map((group) => (
        <div key={group.documentId} className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-600 dark:bg-gray-800/50 dark:text-gray-300">
            {group.label}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  {!readOnly && <th className="w-10 px-4 py-3" />}
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Version</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Direction</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Round</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Parse status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Executed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {group.rows.map((v) => {
                  const immutable = v.direction === 'sent' || v.is_executed === true;
                  const supersededBy = supersededByVersionNo.get(v.id);
                  const supersedesNo = supersedesVersionNo.get(v.id);
                  return (
                    <Fragment key={v.id}>
                    <tr
                      data-testid={`ver-row-${v.id}`}
                      className={supersededBy ? 'opacity-60' : undefined}
                    >
                      {!readOnly && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            data-testid={`ver-select-${v.id}`}
                            checked={selected.has(v.id)}
                            onChange={() => onToggleSelect(v.id)}
                            aria-label={`Select version ${v.version_no} for compare`}
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium tabular-nums text-gray-900 dark:text-gray-100">v{v.version_no}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{v.filename}</div>
                        {supersededBy && (
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400" title="§4.5, R32 — corrected by a superseding row; the old row was never edited">
                            superseded by v{supersededBy}
                          </div>
                        )}
                        {supersedesNo !== undefined && (
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400" title="§4.5, R32">
                            supersedes v{supersedesNo}
                          </div>
                        )}
                        {v.source === 'export' && (
                          <div className="mt-1">
                            <span
                              data-testid={`ver-excel-check-${v.id}`}
                              className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                              title="Excel check: unverified — §8.6, §12.4 tier 4: a person opens the first export of each path in Excel desktop and records the result; no API exposes that recorded flag yet [OWNER]"
                            >
                              Excel check: unverified
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <StatusPill text={DIRECTION_LABEL[v.direction] ?? v.direction} cls={DIRECTION_CLS[v.direction] ?? DIRECTION_CLS.internal} />
                      </td>
                      <td className="px-4 py-3 align-top tabular-nums text-gray-700 dark:text-gray-300">{v.round ?? '—'}</td>
                      <td
                        className="px-4 py-3 align-top tabular-nums text-gray-700 dark:text-gray-300"
                        title={`version_date — extracted: set at approval (§4.5)`}
                      >
                        {v.version_date ?? '—'}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <StatusPill text={v.parse_status} cls={PARSE_STATUS_CLS[v.parse_status] ?? PARSE_STATUS_CLS.pending} />
                      </td>
                      <td className="px-4 py-3 align-top">
                        {v.is_executed ? (
                          <span
                            data-testid={`ver-executed-${v.id}`}
                            className="inline-flex items-center gap-1 text-green-700 dark:text-green-400"
                            title="is_executed — §4.5: the executed flag (D19)"
                          >
                            <CheckCircle2 size={14} /> Executed
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">—</span>
                        )}
                        {immutable && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                            title="Immutable record — §4.5, R32: a version's bytes are never edited in place; the only correction is Supersede"
                          >
                            <Lock size={11} /> record
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            data-testid={`ver-download-${v.id}`}
                            onClick={() => onDownload(v.id)}
                            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                          >
                            <Download size={13} /> Download
                          </button>
                          {!readOnly && !immutable && (
                            <button
                              type="button"
                              data-testid={`ver-executed-${v.id}`}
                              onClick={() => onMarkExecuted(v.id)}
                              disabled={executingId === v.id}
                              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                            >
                              <CheckCircle2 size={13} /> Mark executed
                            </button>
                          )}
                          {!readOnly && !immutable && (
                            <button
                              type="button"
                              data-testid={`ver-supersede-${v.id}`}
                              onClick={() => onOpenSupersede(v.id)}
                              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                            >
                              <History size={13} /> Supersede
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={readOnly ? 7 : 8} className="bg-gray-50/50 px-4 pb-3 pt-0 dark:bg-gray-900/20">
                        <InventoryDisclosure versionId={v.id} parseInventory={v.parseInventory} parseStatus={v.parse_status} />
                      </td>
                    </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

export default VersionList;
